"use client";

import { useEffect, useRef, useState } from "react";

type FloatingCubesProps = {
  cubeCount?: number;
  color?: string;
  speed?: number;
  zIndexClassName?: string;
  safeZoneRatio?: number;
};

export default function FloatingCubes({
  cubeCount = 30,
  color = "#fcad16",
  speed = 0.2, 
  zIndexClassName = "z-10",
  safeZoneRatio = 0.8,
}: FloatingCubesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const [adjustedCount, setAdjustedCount] = useState(cubeCount);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setAdjustedCount(Math.floor(cubeCount * 0.2));
      else if (width < 1024) setAdjustedCount(Math.floor(cubeCount * 0.5));
      else setAdjustedCount(cubeCount);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [cubeCount]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const parent = canvas.parentElement;
    const dpr = window.devicePixelRatio || 1;

    function resize() {
      const w = parent?.clientWidth || window.innerWidth;
      const h = parent?.clientHeight || window.innerHeight;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.clientWidth;
    const H = () => canvas.clientHeight;

    type Cube = {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      size: number;
    };

    const cubes: Cube[] = [];

    const borderRatio = (1 - safeZoneRatio) / 2;

    function spawnInBorder() {
      const side = Math.floor(Math.random() * 4);
      let x, y;

      switch (side) {
        case 0:
          x = Math.random() * W();
          y = Math.random() * H() * borderRatio;
          break;
        case 1:
          x = Math.random() * W();
          y = H() - Math.random() * H() * borderRatio;
          break;
        case 2:
          x = Math.random() * W() * borderRatio;
          y = Math.random() * H();
          break;
        default:
          x = W() - Math.random() * W() * borderRatio;
          y = Math.random() * H();
          break;
      }

      return { x: x - W() / 2, y: y - H() / 2 };
    }

    for (let i = 0; i < adjustedCount; i++) {
      const pos = spawnInBorder();
      cubes.push({
        x: pos.x,
        y: pos.y,
        z: Math.random() * 800 - 400,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        vz: (Math.random() - 0.5) * speed,
        size: Math.random() * 20 + 10,
      });
    }

    function hexToRgba(hex: string, opacity: number) {
      const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return res
        ? `rgba(${parseInt(res[1], 16)},${parseInt(res[2], 16)},${parseInt(res[3], 16)},${opacity})`
        : `rgba(252,173,22,${opacity})`;
    }

    function getFade(x: number, y: number) {
      const cx = W() / 2;
      const cy = H() / 2;
      const dx = Math.abs(x - cx) / (W() / 2);
      const dy = Math.abs(y - cy) / (H() / 2);
      const dist = Math.max(dx, dy);
      return Math.min(1, Math.max(0, (dist - safeZoneRatio) * (1 / (1 - safeZoneRatio))));
    }

    function drawCube(cube: Cube) {
      const { x, y, z, size } = cube;
      if (z < -480 || z > 480) return;
      const half = size / 2;
      const points = [
        { x: x - half, y: y - half, z: z - half },
        { x: x + half, y: y - half, z: z - half },
        { x: x + half, y: y + half, z: z - half },
        { x: x - half, y: y + half, z: z - half },
        { x: x - half, y: y - half, z: z + half },
        { x: x + half, y: y - half, z: z + half },
        { x: x + half, y: y + half, z: z + half },
        { x: x - half, y: y + half, z: z + half },
      ];
      const projected = points.map((p) => {
        const scale = 500 / (500 + p.z);
        return {
          x: p.x * scale + W() / 2,
          y: p.y * scale + H() / 2,
        };
      });
      const fade = getFade(W() / 2 + x, H() / 2 + y);
      const faces = [[0, 1, 2, 3], [4, 5, 6, 7], [0, 1, 5, 4], [2, 3, 7, 6], [1, 2, 6, 5], [0, 3, 7, 4]];
      ctx.strokeStyle = hexToRgba(color, 0.5 * fade);
      ctx.fillStyle = hexToRgba(color, 0.08 * fade);
      for (const f of faces) {
        ctx.beginPath();
        ctx.moveTo(projected[f[0]].x, projected[f[0]].y);
        for (let i = 1; i < 4; i++) {
          ctx.lineTo(projected[f[i]].x, projected[f[i]].y);
        }
        ctx.closePath();
        ctx.stroke();
        ctx.fill();
      }
    }

    function animate() {
      ctx.clearRect(0, 0, W(), H());

      const safeZone = {
        x1: W() * (0.5 - safeZoneRatio / 2),
        x2: W() * (0.5 + safeZoneRatio / 2),
        y1: H() * (0.5 - safeZoneRatio / 2),
        y2: H() * (0.5 + safeZoneRatio / 2),
      };

      for (const cube of cubes) {
        cube.x += cube.vx;
        cube.y += cube.vy;
        cube.z += cube.vz;

        cube.z = Math.max(-400, Math.min(400, cube.z));

        const px = cube.x + W() / 2;
        const py = cube.y + H() / 2;

        // Bounce at canvas edge
        if ((px > W() && cube.vx > 0) || (px < 0 && cube.vx < 0)) cube.vx *= -1;
        if ((py > H() && cube.vy > 0) || (py < 0 && cube.vy < 0)) cube.vy *= -1;

        // Bounce off safe zone - FIXED LOGIC
        if (
          px > safeZone.x1 && px < safeZone.x2 &&
          py > safeZone.y1 && py < safeZone.y2
        ) {
          if (px > safeZone.x1 && px < safeZone.x2) cube.vx *= -1;
          if (py > safeZone.y1 && py < safeZone.y2) cube.vy *= -1;

          // Push cube outside safe zone slightly
          cube.x += cube.vx * 5;
          cube.y += cube.vy * 5;
        }

        drawCube(cube);
      }

      rafRef.current = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [adjustedCount, color, speed, safeZoneRatio]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${zIndexClassName}`}
      aria-hidden="true"
    />
  );
}
